/**
 * The reader's light/dark choice: where it is kept, and how it reaches the document.
 *
 * This lived inside `routes/app/Shell.tsx`, which meant it was applied when the SHELL mounted —
 * that is, after sign-in. Everything before that point, the sign-in page included, painted whatever
 * the operating system preferred and ignored a choice the reader had made in this very browser.
 * The sign-in panel is the one screen in the product that is nothing but brand, so the theme is
 * most visible exactly where it was not being applied (ADR 0089, second amendment).
 *
 * So the module moved out here and `main.tsx` calls `applyTheme(cachedTheme())` before React
 * renders: the first paint of every console route AND every auth route is already the reader's
 * theme. The shell still reconciles against the server when it mounts, because the cache is one
 * browser's and the account's preference is the one that crosses devices.
 *
 * The ceremony is deliberately untouched. It has its own entry (`sign.tsx`, `sign.html`) which does
 * not import this, and `tokens.css` pins `[data-surface='ceremony']` light in every direction — a
 * stranger opening a contract on their phone gets the sender's surface, not ours, and never a dark
 * one (ADR 0081).
 */

/**
 * What `ck_user_ui_preferences__theme` admits. `system` is the schema default and means "follow the
 * device", which is the only honest answer before anybody has chosen.
 */
export type UiTheme = 'system' | 'light' | 'dark'

const THEME_CACHE_KEY = 'flowza.ui_theme'

export function isUiTheme(value: unknown): value is UiTheme {
  return value === 'system' || value === 'light' || value === 'dark'
}

/**
 * Put the theme on the document, which is where `tokens.css` reads it from.
 *
 * The token layer keys off `:root[data-theme='dark']` and `:root:not([data-theme='light'])` inside
 * a `prefers-color-scheme: dark` query, so `system` is the ABSENCE of the attribute rather than a
 * third value — set `data-theme="system"` and the light branch stops matching in a dark OS, which
 * is the one combination that renders unreadable rather than merely wrong.
 */
export function applyTheme(theme: UiTheme): void {
  if (theme === 'system') delete document.documentElement.dataset.theme
  else document.documentElement.dataset.theme = theme
}

/**
 * The last theme this browser saw, read before the API answers.
 *
 * Without it every reload paints the system theme for the length of an authenticated round trip —
 * measured at roughly 1.6 s on production — and somebody who chose dark gets a white flash on every
 * navigation into the shell. This is a per-viewer convenience and nothing depends on it: the
 * server's answer overwrites it the moment it lands, and a browser that refuses storage (a private
 * window, blocked site data) simply starts from `system`, which is what it did before this existed.
 *
 * Wrapped in try/catch because the accessor itself throws in those browsers rather than returning
 * null.
 */
export function cachedTheme(): UiTheme {
  try {
    const stored = window.localStorage.getItem(THEME_CACHE_KEY)
    return isUiTheme(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function cacheTheme(theme: UiTheme): void {
  try {
    window.localStorage.setItem(THEME_CACHE_KEY, theme)
  } catch {
    // A browser that will not store it still renders correctly; there is nothing to report.
  }
}
